import { useInput } from "../hooks/useInput";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  styled
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  getSigningSixprotocolClient,
  sixprotocol,
  cosmos
} from "@sixnetwork/sixchain-sdk";
import { GasPrice } from "@cosmjs/stargate";

import { ethers } from "ethers";
import { useMemo, useState } from "react";
import Web3 from "web3";
import abi from "../abi/transferToCosmos.json";
import chainConfig from "../chainConfig";
import WhiteGradientButton from "../components/WhiteGradientButton";
import union from "../images/union.png";
import BoxStyle from "./BoxStyle";
import Coin from "./Coin";
import FailedBox from "./FailedBox";
import SuccessBox from "./SuccessBox";

const { chainId, rpc } = chainConfig;
const contractABI = abi;
const web3 = new Web3(window.ethereum);
const contract = new web3.eth.Contract(
  contractABI,
  process.env.REACT_APP_CONTRACT_ADDRESS
);

const sendToCosmos = async (dst, amount, from) => {
  try {
    const amountWei = ethers.utils.parseEther(amount.toString());
    const tx = await contract.methods
      .transferToCosmos(dst, amountWei)
      .send({ from });
    return tx;
  } catch (error) {
    throw error;
  }
};

const convertToBillionStr = numStr => {
  // Convert the string to a number and multiply by 1 billion
  let num = parseFloat(numStr);
  let result = num * Math.pow(10, 6);

  return String(result).replace(/\.(\d)+/, "");
};

const signAndBroadcastTransaction = async (dst, amount, from) => {
  if (!window.keplr) {
    alert("Please install Keplr extension to proceed.");
    return;
  }

  // Request Keplr to enable access to the user's wallet
  await window.keplr.enable(chainId);

  // Check if user is using Ledger
  const key = await window.keplr.getKey(chainId);
  const isLedger = key.isNanoLedger;

  // Get the offline signer from Keplr
  const offlineSigner = isLedger
    ? window.getOfflineSignerOnlyAmino(chainId) // Use Amino for Ledger
    : window.getOfflineSigner(chainId);

  const gasPrice = GasPrice.fromString("1.25usix");

  /* 
    const msgUndedelegate = cosmos.staking.v1beta1.MessageComposer.withTypeUrl.undelegate({
    amount: { amount: "1000000", denom: "usix" },
    delegatorAddress: "",
    validatorAddress: ""
    })

      const client = await getSigningCosmosClient({
    rpcEndpoint: rpc,
    signer: offlineSigner,
    options: {
      gasPrice: gasPrice,
    },
  })

    const msgUndedelegate = cosmos.staking.v1beta1.MessageComposer.withTypeUrl.undelegate({
    amount: { amount: "1000000", denom: "usix" },
    delegatorAddress: "",
    validatorAddress: ""
  })

  const txResponse = await client.signAndBroadcast(
    senderAddress.address,
    [msgUndedelegate],
    "auto",
    `undelegate ${dst}`
  );
  */


  // Get signing client
  const client = await getSigningSixprotocolClient({
    rpcEndpoint: rpc,
    signer: offlineSigner,
    options: {
      gasPrice: gasPrice,
    },
  });

  // Get the sender's address
  const [senderAddress] = await offlineSigner.getAccounts();

  const convertAmount = {
    amount: convertToBillionStr(amount),
    denom: "usix",
  };

  const msgWrapToken =
    sixprotocol.tokenmngr.MessageComposer.withTypeUrl.wrapToken({
      amount: convertAmount,
      creator: from,
      receiver: dst, // wrap to evm address
    });

  const txResponse = await client.signAndBroadcast(
    senderAddress.address,
    [msgWrapToken],
    "auto",
    `wrap usix to ${dst}`
  );

  return txResponse;
};

const ToeknStyle = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #c7cbd5;
  border-radius: 12px;
  padding: 16px 24px;
`;

const formatAddress = address => {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const Token = ({ token, address }) => {
  return (
    <ToeknStyle>
      <Coin token={token} size={80} />
      <Typography mt={2} variant="caption">
        {formatAddress(address)}
      </Typography>
    </ToeknStyle>
  );
};

const ConfirmBox = ({ from, to, evmaccount, protocolaccount, customToAddress, onBack }) => {
  const { input } = useInput();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fromMetaMask = useMemo(() => {
    return from.name === "SIX Protocol - EVM";
  }, [from]);

  const fromAddress = useMemo(() => {
    return fromMetaMask ? evmaccount : protocolaccount;
  }, [fromMetaMask, evmaccount, protocolaccount]);

  const toAddress = useMemo(() => {
    return customToAddress || (fromMetaMask ? protocolaccount : evmaccount);
  }, [fromMetaMask, customToAddress, protocolaccount, evmaccount]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const isToEVM = toAddress.startsWith("0x");
      const isFromEVM = fromMetaMask;

      if (isFromEVM && !isToEVM) {
        // EVM -> Cosmos (Bridge)
        await sendToCosmos(toAddress, input, evmaccount);
      } else if (!isFromEVM && isToEVM) {
        // Cosmos -> EVM (Bridge)
        await signAndBroadcastTransaction(
          toAddress,
          input,
          protocolaccount
        );
      } else if (isFromEVM && isToEVM) {
        // EVM -> EVM
        const amountWei = ethers.utils.parseEther(input.toString());
        await web3.eth.sendTransaction({
          from: evmaccount,
          to: toAddress,
          value: amountWei
        });
      } else if (!isFromEVM && !isToEVM) {
        // Cosmos -> Cosmos
        await window.keplr.enable(chainId);
        const key = await window.keplr.getKey(chainId);
        const offlineSigner = key.isNanoLedger ? window.getOfflineSignerOnlyAmino(chainId) : window.getOfflineSigner(chainId);
        const gasPrice = { amount: "1.25", denom: "usix" };
        const client = await getSigningSixprotocolClient({ rpcEndpoint: rpc, signer: offlineSigner });

        const msgSend = cosmos.bank.v1beta1.MessageComposer.withTypeUrl.send({
          amount: [{ amount: convertToBillionStr(input), denom: "usix" }],
          fromAddress: protocolaccount,
          toAddress: toAddress
        });

        await client.signAndBroadcast(protocolaccount, [msgSend], { amount: [gasPrice], gas: "200000" }, "Token Transfer");
      }
      setIsSuccess(true);
      setIsLoading(false);
    } catch (error) {
      setIsFailed(true);
      setIsLoading(false);
      throw error;
    }
  };

  return (
    <>
      <BoxStyle>
        <IconButton
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            paddingTop: 3,
            paddingLeft: 4,
            paddingBottom: 3,
            paddingRight: 3,
            color: "white"
          }}
          onClick={() => onBack(false)}
        >
          <ArrowBackIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <Typography
          variant="h5"
          align="center"
          textTransform="capitalize"
          mb={1}
        >
          Token transfer
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns="1fr auto 1fr"
          columnGap={{ xs: 2, sm: 4 }}
          alignItems="center"
          my={3}
        >
          <Token token={from} address={fromAddress} />
          <img src={union} alt="" height={16} />
          <Token token={to} address={toAddress} />
        </Box>
        <Typography variant="h5" fontWeight="normal" mb={3} lineHeight={1}>
          {input} SIX
        </Typography>

        <Box display="flex" alignItems="center">
          <WhiteGradientButton disabled={isLoading} onClick={handleSubmit}>
            {isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Sending
              </>
            ) : (
              "Send"
            )}
          </WhiteGradientButton>
        </Box>
      </BoxStyle>

      <SuccessBox
        open={isSuccess}
        message="Token Transfer Successful"
        onClose={() => {
          setIsSuccess(false);
          onBack(true);
        }}
      />
      <FailedBox
        open={isFailed}
        message="Something went wrong."
        onClose={() => {
          setIsFailed(false);
          onBack(true);
        }}
      />
    </>
  );
};

export default ConfirmBox;
